import { ref, computed } from "vue";
import { enLocale } from "./en";
import { croLocale } from "./cro";
import { deLocale } from "./de";
import type { LocaleType } from "./en";
import { getStoredLocale, setStoredLocale } from "@/services/native/localePreferences";

const locales = {
  en: enLocale,
  cro: croLocale,
  de: deLocale,
};

type LocaleKey = keyof typeof locales;

const isValidLocale = (locale: string): locale is LocaleKey => {
  return locale in locales;
};

export const currentLocale = ref<LocaleKey>("cro");

// Must be awaited before app mount — native reads Preferences async, so a late call misses first paint.
export async function initLocale(): Promise<void> {
  const savedLocale = await getStoredLocale();
  if (savedLocale && isValidLocale(savedLocale)) {
    currentLocale.value = savedLocale;
  }
}

const t = computed<LocaleType>(() => {
  const key = currentLocale.value;
  return Object.prototype.hasOwnProperty.call(locales, key)
    ? locales[key as LocaleKey] // nosemgrep: gitlab.eslint.detect-object-injection
    : locales.cro;
});

const interpolate = (
  template: string,
  values: Record<string, string>
): string => {
  return template.replace(/\{([^}]+)\}/g, (match, key) => {
    if (Object.prototype.hasOwnProperty.call(values, key)) {
      return Object.getOwnPropertyDescriptor(values, key)?.value ?? match;
    }
    return match;
  });
};

export const useI18n = () => {
  const setLocale = (locale: LocaleKey) => {
    if (isValidLocale(locale)) {
      currentLocale.value = locale;
      void setStoredLocale(locale);
    }
  };

  const getLocale = () => currentLocale.value;
  const getAvailableLocales = () => Object.keys(locales) as LocaleKey[];
  const getText = (path: string, values?: Record<string, string>): string => {
    const keys = path.split(".");
    let result: unknown = t.value;

    for (const key of keys) {
      if (
        result &&
        typeof result === "object" &&
        Object.prototype.hasOwnProperty.call(result, key)
      ) {
        const descriptor = Object.getOwnPropertyDescriptor(result, key);
        result = descriptor?.value;
      } else {
        return path;
      }
    }

    if (typeof result === "string" && values) {
      return interpolate(result, values);
    }

    return typeof result === "string" ? result : path;
  };

  return {
    t,
    setLocale,
    getLocale,
    getAvailableLocales,
    getText,
    currentLocale: computed(() => currentLocale.value),
  };
};
